/**
 * Simple logger system with the possibility of registering custom outputs.
 *
 * 4 different log levels are provided, with corresponding methods:
 * - debug   : for debug information
 * - info    : for informative status of the application (success, ...)
 * - warning : for non-critical errors that do not prevent normal application behavior
 * - error   : for critical errors that prevent normal application behavior
 *
 * Example usage:
 * ```
 * import { Logger } from 'app/core/logger.service';
 *
 * const log = new Logger('myFile');
 * ...
 * log.debug('something happened');
 * ```
 *
 * To disable debug and info logs in production, add this snippet to your root component:
 * ```
 * export class AppComponent implements OnInit {
 *   ngOnInit() {
 *     if (environment.production) {
 *       Logger.enableProductionMode();
 *     }
 *     ...
 *   }
 * }
 *
 * If you want to process logs through other outputs than console, you can add LogOutput functions to Logger.outputs.
 */

/**
 * The possible log levels.
 * LogLevel.Off is never emitted and only used with Logger.level property to disable logs.
 */
export enum LogLevel {
  Off = 0,
  Error,
  Warning,
  Info,
  Debug,
}

/**
 * Log output handler function.
 */
export type LogOutput = (source: string | undefined, level: LogLevel, ...objects: any[]) => void;

export class Logger {
  /**
   * Current logging level.
   * Set it to LogLevel.Off to disable logs completely.
   */
  static level = LogLevel.Debug;

  /**
   * Additional log outputs.
   */
  static outputs: LogOutput[] = [];

  /**
   * Original console methods stored before overriding.
   * Used internally to avoid recursive calls.
   */
  private static originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  /**
   * Enables production mode.
   * Sets logging level to LogLevel.Warning.
   */
  static enableProductionMode() {
    Logger.level = LogLevel.Warning;
  }

  /**
   * Extracts the caller file and line information from the stack trace.
   */
  private static getCallerInfo(): string | undefined {
    try {
      const stack = new Error().stack;
      if (!stack) return undefined;

      const lines = stack.split('\n');
      // Skip the first 4 lines: Error, getCallerInfo, the logger method, and the console override
      for (let i = 4; i < lines.length; i++) {
        const line = lines[i];
        // Match file paths and line numbers
        const match = line.match(/\((.+?):(\d+):(\d+)\)/) || line.match(/at (.+?):(\d+):(\d+)/);
        if (match) {
          const fullPath = match[1];
          const lineNumber = match[2];
          // Extract just the filename from the full path
          let fileName = fullPath.split(/[\\/]/).pop() || fullPath;
          
          // Handle webpack bundle names - extract the last meaningful part
          // e.g., "default-src_app_shared_analytics_constants_ts-src_app_shared_services_analytics_service_ts.js"
          // becomes "analytics_service_ts.js"
          if (fileName.includes('-src_') || fileName.includes('_ts.js') || fileName.includes('_ts-')) {
            const parts = fileName.split('-');
            const lastPart = parts[parts.length - 1];
            // Convert underscores back to dots for readability
            fileName = lastPart.replace(/_ts\.js$/, '.ts').replace(/_/g, '.');
          }
          
          return `${fileName}:${lineNumber}`;
        }
      }
    } catch (e) {
      // If stack trace parsing fails, return undefined
    }
    return undefined;
  }

  /**
   * Overrides the global console methods to use the Logger service.
   * Call this method early in your application initialization (e.g., in main.ts or app.component.ts).
   */
  static overrideConsole() {
    console.log = (...args: any[]) => {
      const source = Logger.getCallerInfo();
      const logger = new Logger(source);
      logger.debug(...args);
    };
    console.info = (...args: any[]) => {
      const source = Logger.getCallerInfo();
      const logger = new Logger(source);
      logger.info(...args);
    };
    console.warn = (...args: any[]) => {
      const source = Logger.getCallerInfo();
      const logger = new Logger(source);
      logger.warn(...args);
    };
    console.error = (...args: any[]) => {
      const source = Logger.getCallerInfo();
      const logger = new Logger(source);
      logger.error(...args);
    };
  }

  constructor(private source?: string) {}

  /**
   * Logs messages or objects  with the debug level.
   * Works the same as console.log().
   */
  debug(...objects: any[]) {
    this.log(Logger.originalConsole.log, LogLevel.Debug, objects);
  }

  /**
   * Logs messages or objects  with the info level.
   * Works the same as console.log().
   */
  info(...objects: any[]) {
    this.log(Logger.originalConsole.info, LogLevel.Info, objects);
  }

  /**
   * Logs messages or objects  with the warning level.
   * Works the same as console.log().
   */
  warn(...objects: any[]) {
    this.log(Logger.originalConsole.warn, LogLevel.Warning, objects);
  }

  /**
   * Logs messages or objects  with the error level.
   * Works the same as console.log().
   */
  error(...objects: any[]) {
    this.log(Logger.originalConsole.error, LogLevel.Error, objects);
  }

  private log(func: (...args: any[]) => void, level: LogLevel, objects: any[]) {
    if (level <= Logger.level) {
      const log = this.source ? ['[' + this.source + ']'].concat(objects) : objects;
      func.apply(console, log);
      Logger.outputs.forEach((output) => output.apply(output, [this.source, level, ...objects]));
    }
  }
}
